import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { classNames, formatBytes, formatDate, visibilityBadgeClass, visibilityLabel, getAttachmentMediaType } from './helpers';
import AttachmentActions from './AttachmentActions.jsx';

const AttachmentListView = ({
  items,
  onEdit,
  onDelete,
  onPreview,
  showInstance = false,
  draggable = false,
  onDragStart = null,
  onDragEnd = null,
  wrapResponsive = true,
  deleteConfirmPrompt = 'Confirm delete?',
}) => {
  const [expandedDescId, setExpandedDescId] = useState(null);

  const columnCount = useMemo(() => (6 + (showInstance ? 1 : 0) + (onPreview ? 1 : 0)), [showInstance, onPreview]);

  const toggleDescription = (id) => {
    setExpandedDescId((prev) => (String(prev) === String(id) ? null : id));
  };

  const table = (
    <table className="table table-striped table-sm">
      <thead>
        <tr>
          <th>Title</th>
          <th>Visibility</th>
          <th>Owner</th>
          {showInstance && <th>Instance</th>}
          <th>Size</th>
          <th>Updated</th>
          {onPreview && <th className="text-center">Preview</th>}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr>
            <td colSpan={columnCount} className="py-4 text-center text-muted">No attachments found</td>
          </tr>
        )}
        {items.map((item) => (
          <tr
            key={item.id}
            draggable={draggable}
            onDragStart={draggable && typeof onDragStart === 'function' ? (e) => onDragStart(e, item) : undefined}
            onDragEnd={draggable && typeof onDragEnd === 'function' ? () => onDragEnd() : undefined}
            style={draggable ? { cursor: 'grab' } : undefined}
          >
            <td>
              <div style={{ fontWeight: 'bold' }}>{item.title || `Attachment #${item.id}`}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{item.mime_type || 'Unknown type'}</div>

              {item.description ? (
                <div style={{ marginTop: 4 }}>
                  <a
                    href="#"
                    className="small"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleDescription(item.id);
                    }}
                  >
                    {String(expandedDescId) === String(item.id) ? 'Hide description' : 'Show description'}
                  </a>
                  {String(expandedDescId) === String(item.id) ? (
                    <div className="text-muted" style={{ marginTop: 4, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                      {item.description}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {item.tags_list?.length > 0 ? (
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Tags: {item.tags_list.join(', ')}
                </div>
              ) : null}
            </td>
            <td>
              <span className={classNames('label', item?.is_private ? 'label-danger' : 'label-success')}>
                {visibilityLabel(item)}
              </span>
            </td>
            <td>{item.owner?.name || item.owner_name || '—'}</td>
            {showInstance && (
              <td>
                <div title={item.instance?.title || `Instance #${item.instance?.id || '—'}`}>
                  {item.instance?.title || `#${item.instance?.id || '—'}`}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>#{item.instance?.id || '—'}</div>
              </td>
            )}
            <td>{formatBytes(item.size_bytes)}</td>
            <td>{formatDate(item.updated_at)}</td>
            {onPreview && (
              <td className="text-center">
                <button
                  type="button"
                  className="btn btn-link btn-xs"
                  onClick={() => onPreview(item)}
                >
                  {getAttachmentMediaType(item) === 'video' ? 'Play' : 'Preview'}
                </button>
              </td>
            )}
            <td className="text-right">
              <AttachmentActions
                attachment={item}
                onEdit={onEdit}
                onDelete={onDelete}
                minimal
                className="text-left"
                confirmPrompt={deleteConfirmPrompt}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (!wrapResponsive) {
    return table;
  }

  return (
    <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'visible' }}>
      {table}
    </div>
  );
};

AttachmentListView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  showInstance: PropTypes.bool,
  draggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  wrapResponsive: PropTypes.bool,
  deleteConfirmPrompt: PropTypes.string,
};

AttachmentListView.defaultProps = {
  onPreview: null,
  showInstance: false,
  draggable: false,
  onDragStart: null,
  onDragEnd: null,
  wrapResponsive: true,
  deleteConfirmPrompt: 'Confirm delete?',
};

export default AttachmentListView;
